import { css } from '@emotion/react'
import { FunctionComponent, memo, useCallback } from 'react'
import type { AvailableSpaceContentType } from '../../../../../types'
import { saveSettings } from '../../../../actions/settings'
import { SettingsType } from '../../../../constants/enums'
import IconButton from '../../../Buttons/IconButton'
import type { IconProps } from '../../../Icons'
import AccountDetailsIcon from '../../../Icons/AccountDetails'
import CircleOffIcon from '../../../Icons/CircleOff'
import GridIcon from '../../../Icons/Grid'
import MessageIcon from '../../../Icons/Message'
import RecommendationIcon from '../../../Icons/Recommendation'
import RoomIcon from '../../../Icons/Room'
import WhiteboardIcon from '../../../Icons/Whiteboard'
import { palette } from '../../../styles/colors'
import { COMPONENT_HEIGHTS } from '../../../theme'

const rootStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `3px 6px`,
})
const iconButtonStyles = css({
    position: 'relative',
    height: COMPONENT_HEIGHTS.availableSpaceControlBar,
    marginLeft: 2,
    marginRight: 2,
    '& .mfc-button': {
        transform: 'scale(0.8)',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 8,
        right: -3,
        width: 1,
        height: 24,
        background: palette.displayModeDivider,
    },
    '&:last-child': {
        '&::after': {
            visibility: 'hidden',
        },
    },
})

const activeIconStyles = css({
    cursor: 'default',
})

interface IButton {
    contentType: AvailableSpaceContentType
    title: string
    Icon: FunctionComponent<IconProps>
    size: number
}

const buttons = (modelName: string): Array<IButton> => [
    {
        contentType: 'profile',
        title: `${modelName}'s Profile`,
        Icon: AccountDetailsIcon,
        size: 1.0,
    },
    {
        contentType: 'whiteboard',
        title: `${modelName}'s Whiteboard`,
        Icon: WhiteboardIcon,
        size: 1.0,
    },
    {
        contentType: 'pm',
        title: `Private Message with ${modelName}`,
        Icon: MessageIcon,
        size: 1.0,
    },
    {
        contentType: 'room',
        title: `${modelName}'s Chat Room`,
        Icon: RoomIcon,
        size: 1.0,
    },
    {
        contentType: 'myml',
        title: 'Models You May Like',
        Icon: RecommendationIcon,
        size: 1.0,
    },
    {
        contentType: 'models',
        title: 'Online Models',
        Icon: GridIcon,
        size: 1.0,
    },
    {
        contentType: 'none',
        title: 'Disable',
        Icon: CircleOffIcon,
        size: 1.0,
    },
]

interface ISelectorButton {
    contentType: AvailableSpaceContentType
    title: string
    isActive: boolean
    size: number
    Icon: FunctionComponent<IconProps>
}

const SelectorButton = memo((props: ISelectorButton) => {
    const { contentType, title, isActive, size, Icon } = props
    const onClick = useCallback(() => {
        saveSettings(SettingsType.ROOM, 'availableSpaceView', contentType)
    }, [contentType])
    return (
        <div css={iconButtonStyles}>
            <IconButton dim={!isActive} title={title} onClick={onClick}>
                <Icon
                    css={isActive ? activeIconStyles : null}
                    size={size || 1}
                    filled={isActive}
                    data-testid={`Button__${contentType}`}
                />
            </IconButton>
        </div>
    )
})

interface ISelectorToolbar {
    contentType: AvailableSpaceContentType
    hide?: Set<AvailableSpaceContentType>
    modelName: string
}

const SelectorToolbar = memo(
    ({ contentType, hide, modelName }: ISelectorToolbar) => (
        <div css={rootStyles} data-testid="Container__ButtonDisplayType">
            {buttons(modelName)
                .filter(
                    (button) =>
                        button.contentType === contentType ||
                        !hide?.has(button.contentType),
                )
                .map((button) => {
                    const isActive = button.contentType === contentType
                    const key = button.contentType + (isActive ? '-active' : '')
                    return (
                        <SelectorButton
                            key={key}
                            {...button}
                            isActive={isActive}
                        />
                    )
                })}
        </div>
    ),
)

export default SelectorToolbar
